import React, { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { User } from '../../types';
import { resolveTester } from '../../utils/tester';
import { loginPathForPortal, safePortalNext } from '../../utils/portalNext';
import BetaLandingPage from './BetaLandingPage';
import BetaAuthPage from './BetaAuthPage';
import PublicDeployTestPage from './PublicDeployTestPage';
import BetaHomeView from './BetaHomeView';
import BetaTakeTestPage from './BetaTakeTestPage';

interface BetaPortalViewProps {
  user: User | null;
  onAuth: (user: User) => void;
  onLogout: () => void;
}

const LEFT_BETA_FLAG = 'fluora-beta-just-left';
const LEFT_BANNER_MS = 10000;

const BetaPortalView: React.FC<BetaPortalViewProps> = ({ user, onAuth, onLogout }) => {
  const location = useLocation();
  const [leftBanner, setLeftBanner] = useState(() => {
    try {
      return sessionStorage.getItem(LEFT_BETA_FLAG) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!leftBanner) return;
    const timer = window.setTimeout(() => {
      setLeftBanner(false);
      try {
        sessionStorage.removeItem(LEFT_BETA_FLAG);
      } catch {
        // Banner cleanup is optional.
      }
    }, LEFT_BANNER_MS);
    return () => window.clearTimeout(timer);
  }, [leftBanner]);

  const path = location.pathname.replace(/\/+$/, '') || '/';
  const isPublicTestPage = path === '/tests' || path.startsWith('/tests/');
  const isTakePage = path.startsWith('/take/');

  const header = (
    <header className="beta-portal-topbar">
      <Link to="/" className="beta-portal-brand">
        <img src="/logo-mark.png" alt="" className="beta-portal-mark" />
        <div>
          <div className="beta-portal-kicker">Fluora Beta</div>
          {user && (
            <div className="beta-portal-user">
              {user.firstName} {user.lastName}
            </div>
          )}
        </div>
      </Link>
      {user ? (
        <button type="button" className="btn btn-outline-light btn-sm" onClick={onLogout}>
          Sign out
        </button>
      ) : (
        <Link to="/login" className="btn btn-outline-light btn-sm">
          Log in
        </Link>
      )}
    </header>
  );

  if (isPublicTestPage) {
    return (
      <div className="beta-portal">
        {header}
        <Routes>
          <Route path="/tests/:name" element={<PublicDeployTestPage />} />
        </Routes>
      </div>
    );
  }

  if (user) {
    if (path === '/login' || path === '/register') {
      return <Navigate to={safePortalNext(location.search)} replace />;
    }

    const tester = resolveTester(user);
    const canTakeTests = tester === 'beta';

    if (isTakePage) {
      if (!canTakeTests) {
        return <Navigate to="/" replace />;
      }
      return (
        <div className="beta-portal">
          {header}
          <Routes>
            <Route path="/take/:testId" element={<BetaTakeTestPage />} />
          </Routes>
        </div>
      );
    }

    return (
      <div className="beta-portal">
        {header}
        <main className="beta-portal-main">
          <BetaHomeView user={user} onAuth={onAuth} />
        </main>
      </div>
    );
  }

  return (
    <div className="beta-portal">
      {header}
      {leftBanner && (
        <div className="beta-left-banner" role="status">
          <p>You have successfully left the Fluora Beta program</p>
          <button
            type="button"
            className="beta-left-banner-close"
            onClick={() => setLeftBanner(false)}
            aria-label="Dismiss"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}
      <Routes>
        <Route path="/" element={<BetaLandingPage />} />
        <Route path="/login" element={<BetaAuthPage mode="login" onAuth={onAuth} />} />
        <Route path="/register" element={<BetaAuthPage mode="register" onAuth={onAuth} />} />
        <Route path="/tests/:name" element={<PublicDeployTestPage />} />
        <Route path="/take/:testId" element={<Navigate to={loginPathForPortal(path)} replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

export default BetaPortalView;
